import type { Espece } from "@repo/types";
import { formatSpecies } from "../../../lib/utils/function-lib";

export interface EspeceSelectOption {
  value: string;
  label: string;
  description?: string;
  group?: string;
  /** Objet Espece complet pour onEspeceSelect */
  data?: Espece;
}

type EspeceLike = {
  id: number;
  name?: string | null;
  nom_fr?: string | null;
  genre?: string | null;
  categorie?: string | null;
  cultivar?: string | null;
  indice_espece?: number | null;
};

type GroupedOption = { label: string; options: EspeceSelectOption[] };

export interface EspecesToSelectOptionsParams {
  groupBy?: "categorie" | "genre" | false;
  formatLabel?: (espece: EspeceLike) => string;
  restrictToIds?: number[];
  includeData?: boolean;
}

/**
 * Transforme une liste d'espèces en options pour ReactSelect.
 * Retourne soit une liste plate, soit une liste groupée selon groupBy.
 */
export function especesToSelectOptions(
  especes: EspeceLike[],
  params: EspecesToSelectOptionsParams = {}
): EspeceSelectOption[] | GroupedOption[] {
  const {
    groupBy = false,
    formatLabel = (e: EspeceLike) => formatSpecies(e as Espece),
    restrictToIds,
    includeData = false,
  } = params;

  let filtered = especes;
  if (restrictToIds && restrictToIds.length > 0) {
    const idSet = new Set(restrictToIds);
    filtered = especes.filter((e) => idSet.has(e.id));
  }

  const toOption = (e: EspeceLike): EspeceSelectOption => ({
    value: e.id.toString(),
    label: formatLabel(e),
    description: e.nom_fr || 'Aucun nom français',
    group: (groupBy === "categorie" ? e.categorie : groupBy === "genre" ? e.genre : undefined) || undefined,
    ...(includeData ? { data: e as Espece } : {}),
  });

  const flatOptions: EspeceSelectOption[] = filtered.map(toOption);

  if (!groupBy) {
    return flatOptions;
  }

  const groupKey = groupBy === "categorie" ? "categorie" : "genre";
  const grouped = filtered.reduce<Record<string, EspeceSelectOption[]>>((acc, espece) => {
    const key = (espece[groupKey] as string) || "Autres";
    if (!acc[key]) acc[key] = [];
    acc[key].push(toOption(espece));
    return acc;
  }, {});

  return Object.entries(grouped).map(([label, options]) => ({ label, options }));
}
